import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GridComponent } from '../../grid.component';
import { ExternalColumnCellEditorParams } from './../ext_column_cell_editor_params.interface';
import { take } from 'rxjs/operators';
import { ZoomResult } from '../../../../../src/lib/domain-models/zoom/zoom-result';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LogService } from '@nts/std/utility';
import { MetaDataUtils } from '../../../../../src/lib/meta-data/meta-data-utils';
import { ExtNewAutocompleteTextBoxComponent } from '../../../../../src/lib/components/controls/core/ext-new-autocomplete-text-box/ext-new-autocomplete-text-box.component';
import { ExternalViewModelInterface, PropertyViewModelInterface, StringPropertyViewModelInterface } from '@nts/std/interfaces';

// create your cellEditor as a Angular component
const KEY_BACKSPACE = 'Backspace';
const KEY_DELETE = 'Delete';
const KEY_F2 = 'F2';
const KEY_ENTER = 'Enter';
const KEY_TAB = 9;
const KEY_UP = 38;
const KEY_DOWN = 40;
const KEY_LEFT = 37;
const KEY_RIGHT = 39;

@UntilDestroy()
@Component({
    selector: 'nts-external-list-cell-editor',
    styleUrls: ['./external-list-cell-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
      ExtNewAutocompleteTextBoxComponent
    ],
    template: `
        <nts-ext-new-autocomplete-text-box
            #autocomplete
            [decodeProperties]="decodeProperties"
            [searchProperties]="searchProperties"
            [externalPropertyViewModel]="externalPropertyViewModel"
            [updateOnBlur]="true"
            [isSearchable]="false"
            [showCodeInDescription]="showCodeInDescription"
            [enableOutsideClickListener]="true"
            [style.width.px]="cellWidth"
            [style.height.px]="cellHeight"
            (valueChange)="valueChange($event)"
            (onFinishEditing)="onFinishEditing()"
            [enableOutsideClickListener]="true"
            >
        </nts-ext-new-autocomplete-text-box>
    `
})

export class ExternalListCellEditorComponent implements OnDestroy, OnInit, AfterViewInit {

    // @ViewChild('autocomplete', { static: false }) autocomplete: ExtNewAutocompleteTextBoxComponent;

    @ViewChild('autocomplete', { static: false }) set content(content: ExtNewAutocompleteTextBoxComponent) {
        this.autocomplete = content;
    }

    decodeProperties: string[] = null;
    searchProperties: string[] = null;

    private autocomplete: ExtNewAutocompleteTextBoxComponent;

    public value: string;
    public highlightAllOnFocus = true;
    public element: ElementRef;
    public zoomPending = false;
    public isCancelled = true;

    private _code: StringPropertyViewModelInterface;

    get code(): StringPropertyViewModelInterface {
        if (!this._code) {
            this._code = this.params.columnInfo.fieldName.split('.').reduce(
                (o, i) => {
                    if (i === 'value') {
                        return o;
                    }
                    return o[i];
                }, this.params.data);
        }
        return this._code;
    }

    get cellWidth() {
        return this.params.eGridCell.clientWidth;
    }

    get cellHeight() {
        return this.params.eGridCell.clientHeight;
    }

    get input(): any {
        if (this.element) {
            return this.element.nativeElement;
        } else {
            return null;
        }
    }

    private _propertyViewModel: StringPropertyViewModelInterface;

    // Usa il backing field
    get propertyViewModel(): StringPropertyViewModelInterface {
        if (!this._propertyViewModel) {
            const associationMetaData = this.externalPropertyViewModel.externalMetaData.associationProperties.find((ass) =>
                MetaDataUtils.toCamelCase(ass.dependentPropertyName) === this.code.propertyName
            );
            const backingFieldPropertyName = MetaDataUtils.toCamelCase(associationMetaData.principalPropertyName);
            const backingField = this.params.node.data[backingFieldPropertyName];
            this._propertyViewModel = backingField;
        }
        return this._propertyViewModel;
    }

    get description(): PropertyViewModelInterface {
        return this.externalPropertyViewModel.automaticDecodeDescription;
    }

    get gridContext(): GridComponent {
        return this.params.context;
    }

    get externalPropertyViewModel(): ExternalViewModelInterface {
        return this.params.node.data[this.params.columnInfo.path];
    }

    get showCodeInDescription(): boolean {
        return this.params.columnInfo.showCodeInDescription ?? this.externalPropertyViewModel.showCode;
    }

    private params: ExternalColumnCellEditorParams;
    private cancelBeforeStart = false;

    constructor(private cd: ChangeDetectorRef) {

    }

    ngOnInit() {
      if (!this.externalPropertyViewModel) { throw new Error('Missing viewModel for externalPropertyViewModel!'); }
      if (this.params?.columnInfo?.decodeProperties?.length > 0) {
          this.decodeProperties = this.params?.columnInfo?.decodeProperties;
      }
      if (this.params?.columnInfo?.searchProperties?.length > 0) {
          this.searchProperties = this.params?.columnInfo?.searchProperties;
      }
    }

    // dont use afterGuiAttached for post gui events - hook into ngAfterViewInit instead for this
    ngAfterViewInit() {
      setTimeout(() => {
          setTimeout(() => {
              this.autocomplete.focus(true);
              this.autocomplete.show();
          });
      });

      this.gridContext.visibleRowsChange.pipe(untilDestroyed(this), take(1)).subscribe(() => {
        this.autocomplete.hide();
        this.onFinishEditing();
      })
   }

    agInit(params: ExternalColumnCellEditorParams): void {
        this.params = params;
        this.setInitialState(this.params);

        this.cancelBeforeStart = false;

        this.externalPropertyViewModel.externalDomainModelChanged.pipe(untilDestroyed(this)).subscribe(() => {
            this.cd.detectChanges();
        });

        this.externalPropertyViewModel.decodeCompleted.pipe(untilDestroyed(this)).subscribe(() => {
            this.cd.detectChanges();
        });
    }

    ngOnDestroy() {
        // Reset backing field
        if (this.isCancelled) {
            this.propertyViewModel.setValue(this.code.getValue());
        }
    }

    isPopup(): boolean {
        return true;
    }

    blur($event) {
        this.propertyViewModel.validate();
    }

    navigateToNextCell(event: any) {

        const key = this.getCharCodeFromEvent(event);

        const fc = this.gridContext.api.getFocusedCell();
        LogService.debug('focused cell rowindex ' + fc.rowIndex, 'focused celll column ' + fc.column);
        if (key === KEY_DOWN) {
            // set selected cell on current cell + 1
            this.gridContext.api.forEachNode((node) => {
                if (fc.rowIndex + 1 === node.rowIndex) {
                    this.gridContext.api.setFocusedCell(node.rowIndex, fc.column);
                    this.gridContext.api.startEditingCell({
                        rowIndex: node.rowIndex,
                        colKey: fc.column
                    });
                }
            });
        } else if (key === KEY_UP) {
            // set selected cell on current cell + 1
            this.gridContext.api.forEachNode((node) => {
                if (fc.rowIndex - 1 === node.rowIndex) {
                    this.gridContext.api.setFocusedCell(node.rowIndex, fc.column);
                    this.gridContext.api.startEditingCell({
                        rowIndex: node.rowIndex,
                        colKey: fc.column
                    });
                }
            });
        } else if (key === KEY_LEFT) {
            this.gridContext.api.tabToPreviousCell();
        } else if (key === KEY_RIGHT) {
            this.gridContext.api.tabToNextCell();
        }
    }

    setInitialState(params: any) {
        let startValue;
        let highlightAllOnFocus = true;

        if (params.eventKey === KEY_BACKSPACE || params.eventKey === KEY_DELETE) {
            // if backspace or delete pressed, we clear the cell
            startValue = '';
        } else if (params.eventKey) {
            // if a letter was pressed, we start with the letter
            startValue = params.eventKey;
            highlightAllOnFocus = false;
        } else {
            // otherwise we start with the current value
            startValue = params.columnInfo.fieldName.split('.').reduce(
                (o, i) => {
                    if (i === 'value') {
                        return o[i];
                    }
                    return o[i];
                }, this.params.data
            )
            if (params.keyPress === KEY_F2) {
                highlightAllOnFocus = false;
            }
        }

        this.value = startValue;
        this.highlightAllOnFocus = highlightAllOnFocus;
    }

    valueChange(value: any) {
        if (this.value != value) {
            this.value = value;
        }
    }

    getValue(): any {
        // Se si esce con il tasto ESC questa funzione non viene chiamata
        this.isCancelled = false;
        return this.value;
    }

    onFinishEditing() {
        this.params.stopEditing();
    }

    isCancelBeforeStart(): boolean {
        return this.cancelBeforeStart;
    }

    onKeyDown(event: any): void {
        if (this.isLeftOrRight(event) || this.deleteOrBackspace(event)) {
            event.stopPropagation();
            return;
        }

        if (event.key === 'F6' && event.ctrlKey && this.externalPropertyViewModel.isEnabled && !this.zoomPending) {
                this.autocomplete.zoom();
        } else if (event.key === 'F8' && event.ctrlKey) {
            event.stopPropagation();
            event.preventDefault();
            event.stopImmediatePropagation();
            this.externalPropertyViewModel.startPresentation();
        }

        if (
            !this.finishedEditingPressed(event) &&
            !this.isKeyPressedString(event)
        ) {
            if (event.preventDefault) { event.preventDefault(); }
        }

        if (this.isUpOrDown(event)) {
            this.navigateToNextCell(event);
        }
    }

    async zoom() {
        if (!this.zoomPending) {
            this.zoomPending = true;
            if (this.externalPropertyViewModel.decodeInProgress) {
                this.externalPropertyViewModel.decodeCompleted.pipe(take(1)).subscribe(async res => {
                    await this.showZoom();
                });
            } else {
                await this.showZoom();
            }
        }
    }

    private async showZoom() {
        const zoomResult = await this.externalPropertyViewModel.findExternal();
        if (zoomResult.result === ZoomResult.empty().result && this.externalPropertyViewModel.hasDecodeError) {
        }
        this.zoomPending = false;

        this.input.focus();
        // this.decodeTooltip.hide();
    }

    private isKeyPressedString(event: any): boolean {
        const charCode = this.getCharCodeFromEvent(event);
        const charStr = event.key ? event.key : String.fromCharCode(charCode);
        return true;
    }

    private getCharCodeFromEvent(event: any): any {
        event = event || window.event;
        return typeof event.which == 'undefined' ? event.keyCode : event.which;
    }

    private deleteOrBackspace(event: any) {
        return (
            [KEY_DELETE, KEY_BACKSPACE].indexOf(this.getCharCodeFromEvent(event)) > -1
        );
    }

    private isLeftOrRight(event: any) {
        return [KEY_LEFT, KEY_RIGHT].indexOf(this.getCharCodeFromEvent(event)) > -1;
    }

    private isUpOrDown(event: any) {
        return [KEY_UP, KEY_DOWN].indexOf(this.getCharCodeFromEvent(event)) > -1;
    }

    private finishedEditingPressed(event: any) {
        const charCode = this.getCharCodeFromEvent(event);
        return charCode === KEY_ENTER || charCode === KEY_TAB;
    }
}
